import React, { useEffect, useState } from "react";
import { useAuth0 } from "../../react-auth0-spa";
import "../../App.css";
import CustomStore from "devextreme/data/custom_store";

import DataGrid, {
  Editing,
  Column,
  Lookup,
  Paging,
  Pager,
  FilterRow,
  RequiredRule,
} from "devextreme-react/data-grid";
import ColorBox from "devextreme-react/color-box";

// Only this user may view/manage the storage-connection mappings. The backend enforces the
// same restriction on all write endpoints; this is purely to hide the UI.
const ADMIN_EMAIL = "fgarcia@elembio.com";

export const StorageMappingsForm = () => {
  const { fetchWithCheck, fetchNoResponse, user } = useAuth0();

  const [connections, setConnections] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    // Real storage connections from the R&D tenant (may be empty if the cloud integration
    // isn't configured yet -> we fall back to free-text entry of the connection ID).
    fetchWithCheck("/storageConnections").then((c) =>
      setConnections(Array.isArray(c) ? c : [])
    );
    fetchWithCheck("/projects").then((p) => setProjects(Array.isArray(p) ? p : []));
    // eslint-disable-next-line
  }, []);

  const isAdmin = user && user.email === ADMIN_EMAIL;

  // --- Group -> storage connection (default) -----------------------------------------
  const groupStore = new CustomStore({
    key: "id",
    // includeDisabled so retired groups are visible/manageable here.
    load: async () => await fetchWithCheck("/groups?includeDisabled=true"),
    insert: async (values) => {
      // New groups always start at runCount 0 (the backend enforces this). enabled
      // defaults to true via onInitNewRow.
      await fetchNoResponse("/groups", {
        method: "POST",
        body: JSON.stringify({
          name: values.name ?? "",
          abbr: values.abbr ?? "",
          color: values.color ?? "",
          storageConnectionID: values.storageConnectionID ?? "",
          enabled: values.enabled ?? true,
        }),
      });
    },
    update: async (key, values) => {
      // Send only the fields that actually changed so a PATCH doesn't clobber the others.
      const body = {};
      for (const f of ["name", "abbr", "color", "storageConnectionID", "enabled"]) {
        if (f in values) body[f] = values[f];
      }
      await fetchNoResponse("/groups/" + key, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
  });

  // --- Project -> storage connection --------------------------------------------------
  const projectStore = new CustomStore({
    key: "id",
    load: async () => await fetchWithCheck("/projectStorageConnections"),
    insert: async (values) => {
      await fetchNoResponse("/projectStorageConnections", {
        method: "POST",
        body: JSON.stringify(values),
      });
    },
    update: async (key, values) => {
      await fetchNoResponse("/projectStorageConnections/" + key, {
        method: "PATCH",
        body: JSON.stringify(values),
      });
    },
    remove: async (key) => {
      await fetchNoResponse("/projectStorageConnections/" + key, {
        method: "DELETE",
      });
    },
  });

  const hasConnections = connections.length > 0;

  // Renders the storageConnectionID column either as a dropdown of real connections (when
  // the cloud list is available) or as a plain free-text field (fallback).
  const renderStorageConnectionColumn = () =>
    hasConnections ? (
      <Column dataField="storageConnectionID" caption="Storage Connection">
        <Lookup
          dataSource={connections}
          valueExpr="id"
          displayExpr={(c) =>
            c ? `${c.name}${c.isDefault ? " (default)" : ""}` : ""
          }
          allowClearing={true}
        />
      </Column>
    ) : (
      <Column
        dataField="storageConnectionID"
        caption="Storage Connection ID"
      />
    );

  // Display cell: a small colored swatch next to the raw color value.
  const renderColorCell = (cell) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          border: "1px solid #ccc",
          background: cell.value || "transparent",
        }}
      />
      <span>{cell.value}</span>
    </div>
  );

  // Edit cell: a real color picker that writes the hex value back to the grid.
  const renderColorEditCell = (cell) => (
    <ColorBox
      defaultValue={cell.value}
      editAlphaChannel={false}
      onValueChanged={(e) => cell.setValue(e.value)}
    />
  );

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <h3>Storage Connection Mappings</h3>
        <p>You are not authorized to view this page.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <br />
      <h3>Storage Connection Mappings</h3>
      <p style={{ color: "#666", maxWidth: 900 }}>
        When a planned run is created without an explicit storage connection, one is
        selected automatically: a <b>project</b> mapping wins, otherwise the run's{" "}
        <b>group</b> default is used, otherwise it is left empty.
        {!hasConnections && (
          <>
            {" "}
            <i>
              (The live storage-connection list is unavailable, so enter the connection
              ID directly.)
            </i>
          </>
        )}
      </p>

      <h4 style={{ marginTop: 24 }}>Group defaults</h4>
      <p style={{ color: "#666", maxWidth: 900 }}>
        Use <b>Add a row</b> to create a new group (name, abbreviation, and color are
        required). Uncheck <b>Enabled</b> to retire a group &mdash; retired groups stay
        here and keep resolving names on historical runs, but are hidden when creating new
        runs.
      </p>
      <DataGrid
        dataSource={groupStore}
        showBorders={true}
        showColumnLines={true}
        showRowLines={true}
        rowAlternationEnabled={true}
        allowColumnResizing={true}
        columnAutoWidth={true}
        onInitNewRow={(e) => {
          // Sensible defaults for a brand-new group.
          e.data.enabled = true;
          e.data.color = "#ffffff";
          e.data.storageConnectionID = "";
        }}
      >
        <FilterRow visible={true} />
        <Editing mode="row" allowAdding={true} allowUpdating={true} />
        <Column dataField="name">
          <RequiredRule />
        </Column>
        <Column dataField="abbr" caption="Abbr">
          <RequiredRule />
        </Column>
        <Column
          dataField="runCount"
          allowEditing={false}
          caption="Run Count"
        />
        <Column
          dataField="color"
          caption="Color"
          cellRender={renderColorCell}
          editCellRender={renderColorEditCell}
        >
          <RequiredRule />
        </Column>
        {renderStorageConnectionColumn()}
        <Column
          dataField="enabled"
          caption="Enabled"
          dataType="boolean"
          width={90}
        />
        <Paging defaultPageSize={40} />
        <Pager showPageSizeSelector={true} allowedPageSizes={[20, 40, 100]} />
      </DataGrid>

      <h4 style={{ marginTop: 32 }}>Project mappings</h4>
      <DataGrid
        dataSource={projectStore}
        showBorders={true}
        showColumnLines={true}
        showRowLines={true}
        rowAlternationEnabled={true}
        allowColumnResizing={true}
        columnAutoWidth={true}
      >
        <FilterRow visible={true} />
        <Editing
          mode="row"
          allowAdding={true}
          allowUpdating={true}
          allowDeleting={true}
        />
        <Column dataField="project" caption="Project">
          <Lookup dataSource={projects} />
        </Column>
        {renderStorageConnectionColumn()}
        <Paging defaultPageSize={40} />
        <Pager showPageSizeSelector={true} allowedPageSizes={[20, 40, 100]} />
      </DataGrid>
      <br />
      <br />
    </div>
  );
};

export default StorageMappingsForm;
